import React, { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import {
  Box,
  Chip,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import type {
  CapabilityMapDataset,
  CapabilityMapFilters,
  CompanyData,
  DomainMode,
  HeatmapMode,
  MarketStatus,
  NodeKind,
  SubCapabilityData,
} from "./types";

interface Props {
  dataset: CapabilityMapDataset;
  filters: CapabilityMapFilters;
  domainMode: DomainMode;
  marketStatusFilter: MarketStatus | null;
  searchQuery: string;
  highlightedNodeIds: Set<string>;
  heatmapMode: HeatmapMode;
  onHeatmapModeChange: (mode: HeatmapMode) => void;
  onNodeClick: (nodeId: string, kind: NodeKind) => void;
}

interface TooltipState {
  x: number;
  y: number;
  rowLabel: string;
  colLabel: string;
  covered: boolean;
  extra?: string;
}

const CELL_H = 28;
const CELL_W = 52;
const ROW_LABEL_W = 220;
const CLUSTER_HEADER_H = 32;
const COL_HEADER_H = 100;
const STATUS_COL_W = 90;

function deriveMarketStatus(companyCount: number): MarketStatus {
  if (companyCount >= 4) return "crowded";
  if (companyCount >= 2) return "moderate";
  return "open";
}

const STATUS_COLORS: Record<MarketStatus, string> = {
  crowded: "#b71c1c",
  moderate: "#f57f17",
  open: "#1b5e20",
};

// ── Component Coverage mode (existing) ──────────────────────────────────────

const ComponentCoverageView: React.FC<{
  dataset: CapabilityMapDataset;
  filters: CapabilityMapFilters;
  domainMode: DomainMode;
  marketStatusFilter: MarketStatus | null;
  searchQuery: string;
  highlightedNodeIds: Set<string>;
  onNodeClick: (nodeId: string, kind: NodeKind) => void;
}> = ({
  dataset,
  filters,
  domainMode,
  marketStatusFilter,
  searchQuery,
  highlightedNodeIds,
  onNodeClick,
}) => {
  const [colSearch, setColSearch] = useState("");
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const companies = useMemo(() => {
    let cos = dataset.companies.filter((c) => {
      if (
        domainMode !== "all" &&
        !c.domains.includes(domainMode as "defense" | "industrial")
      )
        return false;
      if (
        filters.companyTypes.length > 0 &&
        !filters.companyTypes.some((t) => c.companyTypes.includes(t))
      )
        return false;
      return true;
    });
    if (colSearch)
      cos = cos.filter((c) =>
        c.label.toLowerCase().includes(colSearch.toLowerCase()),
      );
    cos = [...cos].sort(
      (a, b) => b.capabilitiesCovered.length - a.capabilitiesCovered.length,
    );
    return cos;
  }, [dataset.companies, domainMode, filters.companyTypes, colSearch]);

  type RowEntry =
    | { type: "cluster"; id: string; label: string }
    | { type: "subCap"; sc: SubCapabilityData; clusterId: string };

  const allowedSubCapIdsBySystemType = useMemo(() => {
    if (filters.systemTypeIds.length === 0) return null;
    const allowedSysCapIds = new Set<string>();
    for (const st of dataset.systemTypes) {
      if (filters.systemTypeIds.includes(st.id)) {
        st.systemCapabilityIds.forEach((id) => allowedSysCapIds.add(id));
      }
    }
    const allowed = new Set<string>();
    for (const cluster of dataset.clusters) {
      for (const sc of cluster.subCapabilities) {
        if (
          (sc.systemCapabilityIds ?? []).some((id) => allowedSysCapIds.has(id))
        ) {
          allowed.add(sc.id);
        }
      }
    }
    return allowed;
  }, [dataset.systemTypes, dataset.clusters, filters.systemTypeIds]);

  const rows = useMemo<RowEntry[]>(() => {
    const result: RowEntry[] = [];
    for (const cluster of dataset.clusters) {
      const subCaps = cluster.subCapabilities.filter((sc) => {
        if (
          allowedSubCapIdsBySystemType &&
          !allowedSubCapIdsBySystemType.has(sc.id)
        )
          return false;
        if (
          filters.useCaseTags.length > 0 &&
          !sc.useCases.some((uc) =>
            filters.useCaseTags.some((t) => uc.useCaseTags.includes(t)),
          )
        ) {
          return false;
        }
        if (marketStatusFilter) {
          const count = companies.filter((c) =>
            c.capabilitiesCovered.includes(sc.id),
          ).length;
          if (deriveMarketStatus(count) !== marketStatusFilter) return false;
        }
        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          if (
            !sc.label.toLowerCase().includes(q) &&
            !cluster.label.toLowerCase().includes(q)
          )
            return false;
        }
        return true;
      });
      if (subCaps.length === 0) continue;
      result.push({ type: "cluster", id: cluster.id, label: cluster.label });
      for (const sc of subCaps)
        result.push({ type: "subCap", sc, clusterId: cluster.id });
    }
    return result;
  }, [
    dataset.clusters,
    companies,
    marketStatusFilter,
    searchQuery,
    filters.useCaseTags,
    allowedSubCapIdsBySystemType,
  ]);

  const colorScale = d3.scaleSequential(d3.interpolateBlues).domain([0, 1]);
  const totalW = ROW_LABEL_W + companies.length * CELL_W + STATUS_COL_W;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: "divider" }}>
        <TextField
          size="small"
          placeholder="Filter companies…"
          value={colSearch}
          onChange={(e) => setColSearch(e.target.value)}
          sx={{ width: 240 }}
          inputProps={{ style: { fontSize: 13 } }}
        />
        <Typography variant="caption" sx={{ ml: 2, color: "text.secondary" }}>
          {companies.length} companies ·{" "}
          {rows.filter((r) => r.type === "subCap").length} sub-capabilities
        </Typography>
      </Box>

      <Box
        ref={containerRef}
        sx={{ flex: 1, overflow: "auto", position: "relative" }}
      >
        <Box sx={{ minWidth: totalW, position: "relative" }}>
          {/* Column headers */}
          <Box
            sx={{
              display: "flex",
              position: "sticky",
              top: 0,
              zIndex: 3,
              bgcolor: "background.paper",
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                width: ROW_LABEL_W,
                minWidth: ROW_LABEL_W,
                height: COL_HEADER_H,
                position: "sticky",
                left: 0,
                zIndex: 4,
                bgcolor: "background.paper",
                borderRight: 1,
                borderColor: "divider",
                display: "flex",
                alignItems: "flex-end",
                px: 1,
                pb: 0.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 600 }}
              >
                Sub-Capability
              </Typography>
            </Box>
            {companies.map((c) => (
              <Box
                key={c.id}
                sx={{
                  width: CELL_W,
                  minWidth: CELL_W,
                  height: COL_HEADER_H,
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  pb: 0.75,
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                  opacity:
                    highlightedNodeIds.size > 0 && !highlightedNodeIds.has(c.id)
                      ? 0.3
                      : 1,
                  transition: "opacity 0.2s",
                }}
                onClick={() => onNodeClick(c.id, "company")}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.65rem",
                    writingMode: "vertical-rl",
                    transform: "rotate(180deg)",
                    color: highlightedNodeIds.has(c.id)
                      ? "primary.main"
                      : "text.primary",
                    fontWeight: highlightedNodeIds.has(c.id) ? 700 : 400,
                    whiteSpace: "nowrap",
                    maxHeight: 90,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {c.label}
                </Typography>
              </Box>
            ))}
            <Box
              sx={{
                width: STATUS_COL_W,
                minWidth: STATUS_COL_W,
                height: COL_HEADER_H,
                position: "sticky",
                right: 0,
                bgcolor: "background.paper",
                borderLeft: 1,
                borderColor: "divider",
                display: "flex",
                alignItems: "flex-end",
                px: 1,
                pb: 0.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 600 }}
              >
                Market
              </Typography>
            </Box>
          </Box>

          {/* Rows */}
          {rows.map((row) => {
            if (row.type === "cluster") {
              return (
                <Box
                  key={`cluster-${row.id}`}
                  sx={{
                    display: "flex",
                    height: CLUSTER_HEADER_H,
                    bgcolor: "rgba(21, 101, 192, 0.08)",
                    borderBottom: 1,
                    borderColor: "divider",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: ROW_LABEL_W,
                      minWidth: ROW_LABEL_W,
                      position: "sticky",
                      left: 0,
                      bgcolor: "rgba(21, 101, 192, 0.12)",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      px: 1.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "primary.main",
                        fontSize: "0.75rem",
                      }}
                    >
                      {row.label}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }} />
                  <Box
                    sx={{
                      width: STATUS_COL_W,
                      minWidth: STATUS_COL_W,
                      position: "sticky",
                      right: 0,
                      height: "100%",
                      bgcolor: "rgba(21, 101, 192, 0.12)",
                    }}
                  />
                </Box>
              );
            }

            const { sc } = row as {
              type: "subCap";
              sc: SubCapabilityData;
              clusterId: string;
            };
            const coverCount = companies.filter((c) =>
              c.capabilitiesCovered.includes(sc.id),
            ).length;
            const ms = deriveMarketStatus(coverCount);
            const maxCoverCount = Math.max(1, dataset.companies.length);

            return (
              <Box
                key={sc.id}
                sx={{
                  display: "flex",
                  height: CELL_H,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Box
                  sx={{
                    width: ROW_LABEL_W,
                    minWidth: ROW_LABEL_W,
                    position: "sticky",
                    left: 0,
                    bgcolor: "background.paper",
                    zIndex: 1,
                    display: "flex",
                    alignItems: "center",
                    px: 1.5,
                    borderRight: 1,
                    borderColor: "divider",
                    cursor: "pointer",
                    "&:hover": { "& .row-label": { color: "primary.main" } },
                  }}
                  onClick={() => onNodeClick(sc.id, "sub_capability")}
                >
                  <Typography
                    className="row-label"
                    variant="caption"
                    sx={{
                      fontSize: "0.72rem",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      color: highlightedNodeIds.has(sc.id)
                        ? "primary.main"
                        : "text.primary",
                      fontWeight: highlightedNodeIds.has(sc.id) ? 700 : 400,
                    }}
                  >
                    {sc.label}
                  </Typography>
                </Box>
                {companies.map((c) => {
                  const covered = c.capabilitiesCovered.includes(sc.id);
                  const intensity = covered ? coverCount / maxCoverCount : 0;
                  const bg = covered
                    ? colorScale(0.2 + intensity * 0.8)
                    : "transparent";
                  return (
                    <Box
                      key={c.id}
                      sx={{
                        width: CELL_W,
                        minWidth: CELL_W,
                        height: CELL_H,
                        bgcolor: bg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: covered ? "pointer" : "default",
                        opacity:
                          highlightedNodeIds.size > 0 &&
                          !highlightedNodeIds.has(c.id) &&
                          !highlightedNodeIds.has(sc.id)
                            ? 0.25
                            : 1,
                        transition: "opacity 0.2s",
                        "&:hover": covered ? { filter: "brightness(1.3)" } : {},
                      }}
                      onClick={() => covered && onNodeClick(c.id, "company")}
                      onMouseEnter={(e) => {
                        if (!covered) return;
                        const rect = (
                          e.currentTarget as HTMLElement
                        ).getBoundingClientRect();
                        const cRect =
                          containerRef.current?.getBoundingClientRect();
                        setTooltip({
                          x: rect.left - (cRect?.left ?? 0) + rect.width / 2,
                          y: rect.top - (cRect?.top ?? 0),
                          rowLabel: sc.label,
                          colLabel: c.label,
                          covered,
                        });
                      }}
                      onMouseLeave={() => setTooltip(null)}
                    >
                      {covered && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: "rgba(255,255,255,0.7)",
                          }}
                        />
                      )}
                    </Box>
                  );
                })}
                <Box
                  sx={{
                    width: STATUS_COL_W,
                    minWidth: STATUS_COL_W,
                    position: "sticky",
                    right: 0,
                    bgcolor: "background.paper",
                    borderLeft: 1,
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1,
                  }}
                >
                  <Chip
                    label={ms}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.6rem",
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                      bgcolor: STATUS_COLORS[ms],
                      color: "#fff",
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {tooltip && (
        <Paper
          elevation={4}
          sx={{
            position: "absolute",
            left:
              tooltip.x +
              (containerRef.current?.getBoundingClientRect().left ?? 0),
            top: tooltip.y - 8,
            transform: "translate(-50%, -100%)",
            p: 1,
            pointerEvents: "none",
            zIndex: 20,
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {tooltip.colLabel}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            covers {tooltip.rowLabel}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

// ── Capability Coverage mode (new) ────────────────────────────────────────────

const DEMAND_COL_W = 80;

const CapabilityCoverageView: React.FC<{
  dataset: CapabilityMapDataset;
  onNodeClick: (nodeId: string, kind: NodeKind) => void;
}> = ({ dataset, onNodeClick }) => {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const componentTypes = dataset.componentTypes;

  // For each system capability, which component types provide enabling component capabilities?
  const coverageMatrix = useMemo(() => {
    return dataset.systemCapabilities.map((sc) => {
      const coveredCtIds = new Set<string>();
      componentTypes.forEach((ct) => {
        const enables = ct.componentCapabilityIds.some((ccId) =>
          sc.enabledByComponentCapIds.includes(ccId),
        );
        if (enables) coveredCtIds.add(ct.id);
      });
      return { sc, coveredCtIds };
    });
  }, [dataset.systemCapabilities, componentTypes]);

  const maxDemand = Math.max(
    1,
    ...dataset.systemCapabilities.map((sc) => sc.demandCount),
  );
  const colorScale = d3.scaleSequential(d3.interpolateGreens).domain([0, 1]);
  const totalW = ROW_LABEL_W + componentTypes.length * CELL_W + DEMAND_COL_W;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: "divider" }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {dataset.systemCapabilities.length} system capabilities ×{" "}
          {componentTypes.length} component types · rightmost column = tender
          demand count
        </Typography>
      </Box>

      <Box
        ref={containerRef}
        sx={{ flex: 1, overflow: "auto", position: "relative" }}
      >
        <Box sx={{ minWidth: totalW, position: "relative" }}>
          {/* Column headers */}
          <Box
            sx={{
              display: "flex",
              position: "sticky",
              top: 0,
              zIndex: 3,
              bgcolor: "background.paper",
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                width: ROW_LABEL_W,
                minWidth: ROW_LABEL_W,
                height: COL_HEADER_H,
                position: "sticky",
                left: 0,
                zIndex: 4,
                bgcolor: "background.paper",
                borderRight: 1,
                borderColor: "divider",
                display: "flex",
                alignItems: "flex-end",
                px: 1,
                pb: 0.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 600 }}
              >
                System Capability
              </Typography>
            </Box>
            {componentTypes.map((ct) => (
              <Box
                key={ct.id}
                sx={{
                  width: CELL_W,
                  minWidth: CELL_W,
                  height: COL_HEADER_H,
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  pb: 0.75,
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                }}
                onClick={() => onNodeClick(ct.id, "component_type")}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.65rem",
                    writingMode: "vertical-rl",
                    transform: "rotate(180deg)",
                    whiteSpace: "nowrap",
                    maxHeight: 90,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {ct.label}
                </Typography>
              </Box>
            ))}
            <Box
              sx={{
                width: DEMAND_COL_W,
                minWidth: DEMAND_COL_W,
                height: COL_HEADER_H,
                position: "sticky",
                right: 0,
                bgcolor: "background.paper",
                borderLeft: 1,
                borderColor: "divider",
                display: "flex",
                alignItems: "flex-end",
                px: 1,
                pb: 0.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 600 }}
              >
                Demand
              </Typography>
            </Box>
          </Box>

          {/* Rows: one per system capability, grouped by cluster label */}
          {(() => {
            const clusterLabels = Array.from(
              new Set(dataset.systemCapabilities.map((sc) => sc.clusterLabel)),
            );
            return clusterLabels.map((clLabel) => {
              const scs = coverageMatrix.filter(
                (r) => r.sc.clusterLabel === clLabel,
              );
              return (
                <React.Fragment key={clLabel}>
                  {/* Cluster header */}
                  <Box
                    sx={{
                      display: "flex",
                      height: CLUSTER_HEADER_H,
                      bgcolor: "rgba(0, 105, 92, 0.08)",
                      borderBottom: 1,
                      borderColor: "divider",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: ROW_LABEL_W,
                        minWidth: ROW_LABEL_W,
                        position: "sticky",
                        left: 0,
                        bgcolor: "rgba(0, 105, 92, 0.12)",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        px: 1.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: "#00897B",
                          fontSize: "0.75rem",
                        }}
                      >
                        {clLabel}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Box
                      sx={{
                        width: DEMAND_COL_W,
                        minWidth: DEMAND_COL_W,
                        position: "sticky",
                        right: 0,
                        height: "100%",
                        bgcolor: "rgba(0, 105, 92, 0.12)",
                      }}
                    />
                  </Box>

                  {scs.map(({ sc, coveredCtIds }) => (
                    <Box
                      key={sc.id}
                      sx={{
                        display: "flex",
                        height: CELL_H,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <Box
                        sx={{
                          width: ROW_LABEL_W,
                          minWidth: ROW_LABEL_W,
                          position: "sticky",
                          left: 0,
                          bgcolor: "background.paper",
                          zIndex: 1,
                          display: "flex",
                          alignItems: "center",
                          px: 1.5,
                          borderRight: 1,
                          borderColor: "divider",
                          cursor: "pointer",
                        }}
                        onClick={() => onNodeClick(sc.id, "system_capability")}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.72rem",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {sc.label}
                        </Typography>
                      </Box>
                      {componentTypes.map((ct) => {
                        const covered = coveredCtIds.has(ct.id);
                        const bg = covered
                          ? colorScale(
                              0.45 + 0.55 * (sc.demandCount / maxDemand),
                            )
                          : "transparent";
                        return (
                          <Box
                            key={ct.id}
                            sx={{
                              width: CELL_W,
                              minWidth: CELL_W,
                              height: CELL_H,
                              bgcolor: bg,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: covered ? "pointer" : "default",
                              "&:hover": covered
                                ? { filter: "brightness(1.3)" }
                                : {},
                            }}
                            onClick={() =>
                              covered && onNodeClick(ct.id, "component_type")
                            }
                            onMouseEnter={(e) => {
                              if (!covered) return;
                              const rect = (
                                e.currentTarget as HTMLElement
                              ).getBoundingClientRect();
                              const cRect =
                                containerRef.current?.getBoundingClientRect();
                              setTooltip({
                                x:
                                  rect.left -
                                  (cRect?.left ?? 0) +
                                  rect.width / 2,
                                y: rect.top - (cRect?.top ?? 0),
                                rowLabel: sc.label,
                                colLabel: ct.label,
                                covered,
                                extra: `${sc.demandCount} tender${sc.demandCount !== 1 ? "s" : ""}`,
                              });
                            }}
                            onMouseLeave={() => setTooltip(null)}
                          >
                            {covered && (
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  bgcolor: "rgba(255,255,255,0.7)",
                                }}
                              />
                            )}
                          </Box>
                        );
                      })}
                      {/* Demand count column */}
                      <Box
                        sx={{
                          width: DEMAND_COL_W,
                          minWidth: DEMAND_COL_W,
                          position: "sticky",
                          right: 0,
                          bgcolor: "background.paper",
                          borderLeft: 1,
                          borderColor: "divider",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.5,
                          zIndex: 1,
                        }}
                      >
                        {sc.demandCount > 0 ? (
                          <Chip
                            label={`${sc.demandCount} tender${sc.demandCount !== 1 ? "s" : ""}`}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.6rem",
                              fontWeight: 700,
                              bgcolor:
                                sc.demandCount >= 3
                                  ? "#b71c1c"
                                  : sc.demandCount >= 2
                                    ? "#f57f17"
                                    : "#1b5e20",
                              color: "#fff",
                            }}
                          />
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{ color: "text.disabled", fontSize: "0.6rem" }}
                          >
                            —
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </React.Fragment>
              );
            });
          })()}
        </Box>
      </Box>

      {tooltip && (
        <Paper
          elevation={4}
          sx={{
            position: "absolute",
            left:
              tooltip.x +
              (containerRef.current?.getBoundingClientRect().left ?? 0),
            top: tooltip.y - 8,
            transform: "translate(-50%, -100%)",
            p: 1,
            pointerEvents: "none",
            zIndex: 20,
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {tooltip.colLabel}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block" }}
          >
            enables {tooltip.rowLabel}
          </Typography>
          {tooltip.extra && (
            <Typography variant="caption" sx={{ color: "warning.main" }}>
              {tooltip.extra}
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const CapabilityMapHeatmap: React.FC<Props> = ({
  dataset,
  filters,
  domainMode,
  marketStatusFilter,
  searchQuery,
  highlightedNodeIds,
  heatmapMode,
  onHeatmapModeChange,
  onNodeClick,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Mode toggle */}
      <Box
        sx={{
          px: 2,
          py: 0.75,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontWeight: 600 }}
        >
          Mode:
        </Typography>
        <ToggleButtonGroup
          value={heatmapMode}
          exclusive
          size="small"
          onChange={(_e, v) => {
            if (v) onHeatmapModeChange(v as HeatmapMode);
          }}
          sx={{
            "& .MuiToggleButton-root": {
              px: 1.5,
              py: 0.25,
              fontSize: "0.7rem",
            },
          }}
        >
          <ToggleButton value="component">Component Coverage</ToggleButton>
          <ToggleButton value="capability">Capability Coverage</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ flex: 1, overflow: "hidden", position: "relative" }}>
        {heatmapMode === "component" ? (
          <ComponentCoverageView
            dataset={dataset}
            filters={filters}
            domainMode={domainMode}
            marketStatusFilter={marketStatusFilter}
            searchQuery={searchQuery}
            highlightedNodeIds={highlightedNodeIds}
            onNodeClick={onNodeClick}
          />
        ) : (
          <CapabilityCoverageView dataset={dataset} onNodeClick={onNodeClick} />
        )}
      </Box>
    </Box>
  );
};

export default CapabilityMapHeatmap;
